<template>
    <div class="stat-content" :class="contentClasses">
        <!-- Icon background decoration -->
        <div v-if="icon" class="stat-icon-bg">
            <v-icon :size="iconBgSize" color="primary-light">
                {{ iconName(icon) }}
            </v-icon>
        </div>

        <!-- Main content area -->
        <div class="stat-main">
            <!-- Header section with title and icon -->
            <div class="stat-header">
                <div class="stat-header-left">
                    <h3 class="stat-title">{{ title }}</h3>
                    <div v-if="subtitle" class="stat-subtitle" :class="subtitleClasses">
                        {{ subtitle }}
                    </div>
                </div>
                <button
                    v-if="warning"
                    type="button"
                    class="stat-warning-btn"
                    :class="{ 'stat-warning-btn--active': warningActive }"
                    :aria-label="warningLabel || undefined"
                    :title="warningLabel || undefined"
                    @click.stop="$emit('warning-click', $event)"
                >
                    <v-icon color="warning" :size="warningIconSize">
                        mdi-alert
                    </v-icon>
                </button>
                <div
                    v-else-if="icon && !iconShadowOnly"
                    class="stat-header-icon"
                >
                    <v-icon :size="iconSize" color="primary">
                        {{ iconName(icon) }}
                    </v-icon>
                </div>
            </div>

            <!-- Value section -->
            <div class="stat-values">
                <template v-if="variant === 'create'">
                    <div class="stat-value-wrapper stat-value-wrapper--create">
                        <v-icon color="primary" size="32">{{ iconName(icon || "plus") }}</v-icon>
                    </div>
                </template>
                <template v-else-if="loading">
                    <div class="stat-value stat-value--loading" :class="valueClasses">
                        <div class="stat-loading-spinner"></div>
                        <span>{{ $translate("loading") }}</span>
                    </div>
                    <div
                        v-if="secondaryValue !== null && secondaryValue !== undefined"
                        class="stat-secondary-value stat-secondary-value--loading"
                        :class="secondaryValueClasses"
                    >
                        &nbsp;
                    </div>
                </template>
                <template v-else>
                    <div class="stat-value-wrapper">
                        <div
                            v-if="secondaryAbove && secondaryValue !== null && secondaryValue !== undefined"
                            class="stat-secondary-value"
                            :class="secondaryValueClasses"
                        >
                            {{ formattedSecondaryValue }}
                        </div>
                        <div class="stat-value" :class="valueClasses">
                            {{ formattedStat }}<span
                                v-if="displayValueSuffix"
                                class="stat-value-suffix"
                            >{{ displayValueSuffix }}</span>
                        </div>
                        <div
                            v-if="!secondaryAbove && secondaryValue !== null && secondaryValue !== undefined"
                            class="stat-secondary-value"
                            :class="secondaryValueClasses"
                        >
                            {{ formattedSecondaryValue }}
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <div class="stat-accent-bar" :class="accentBarClasses"></div>
    </div>
</template>

<script>
import GlobalMixins from "@/mixins/global";
import utils from "@/utils";

export default {
    name: "StatContent",
    mixins: [GlobalMixins],
    props: {
        title: {
            type: String,
            required: true,
        },
        icon: {
            type: String,
            default: null,
        },
        size: {
            type: String,
            default: "small",
        },
        stat: {
            type: [String, Number],
            default: null,
        },
        subtitle: {
            type: String,
            default: "",
        },
        secondaryAbove: {
            type: Boolean,
            default: false,
        },
        secondaryValue: {
            type: [String, Number],
            default: null,
        },
        secondaryFormat: {
            type: String,
            default: "number",
        },
        format: {
            type: String,
            default: "number",
        },
        valueSuffix: {
            type: String,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        subtitleColor: {
            type: String,
            default: "grey",
        },
        variant: {
            type: String,
            default: "default",
            validator: (value) => ["default", "create"].includes(value),
        },
        iconShadowOnly: {
            type: Boolean,
            default: false,
        },
        warning: {
            type: Boolean,
            default: false,
        },
        warningActive: {
            type: Boolean,
            default: false,
        },
        warningLabel: {
            type: String,
            default: null,
        },
    },
    computed: {
        warningIconSize() {
            const sizes = {
                compact: 18,
                small: 20,
                medium: 22,
                large: 24,
            };
            return sizes[this.size] || 20;
        },
        contentClasses() {
            return {
                [`stat-content--${this.size}`]: true,
                "stat-content--loading": this.loading,
                "stat-content--create": this.variant === "create",
            };
        },
        iconSize() {
            const sizes = {
                compact: 18,
                small: 24,
                medium: 32,
                large: 40,
            };
            return sizes[this.size] || 24;
        },
        iconBgSize() {
            const sizes = {
                compact: 56,
                small: 80,
                medium: 100,
                large: 120,
            };
            return sizes[this.size] || 80;
        },
        valueClasses() {
            return {
                "stat-value--negative": this.isNegativeValue,
            };
        },
        secondaryValueClasses() {
            return {
                [`stat-secondary-value--${this.subtitleColor}`]: true,
            };
        },
        subtitleClasses() {
            return {
                [`stat-subtitle--${this.subtitleColor}`]: true,
            };
        },
        accentBarClasses() {
            return {
                "stat-accent-bar--negative": this.isNegativeValue,
            };
        },
        isNegativeValue() {
            return (
                this.format === "euros" &&
                this.stat !== null &&
                this.stat !== undefined &&
                Number(this.stat) < 0
            );
        },
        formattedStat() {
            if (this.stat === null || this.stat === undefined) {
                return "-";
            }

            switch (this.format) {
                case "string":
                    return this.stat;
                case "euros":
                    return utils.price(this.stat);
                case "percent":
                    return utils.percent(this.stat);
                case "number":
                default:
                    return utils.formatNumberToString(this.stat);
            }
        },
        displayValueSuffix() {
            if (!this.valueSuffix || this.format !== "euros") {
                return null;
            }
            if (this.loading || this.stat === null || this.stat === undefined) {
                return null;
            }
            return this.valueSuffix;
        },
        formattedSecondaryValue() {
            if (
                this.secondaryValue === null ||
                this.secondaryValue === undefined
            ) {
                return "-";
            }

            switch (this.secondaryFormat) {
                case "percent":
                    return (
                        this.$utils.formatNumberToString(this.secondaryValue, {
                            decimals: 2,
                        }) + "%"
                    );
                case "euros":
                    return this.$utils.price(this.secondaryValue);
                case "string":
                    return this.secondaryValue;
                case "number":
                default:
                    return this.$utils.formatNumberToString(this.secondaryValue);
            }
        },
    },
    i18n: {
        messages: {
            en: {
                loading: "Loading...",
            },
            fr: {
                loading: "Chargement...",
            },
        },
    },
};
</script>

<style scoped lang="scss">
.stat-content {
    flex: 1 1 auto;
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 110px;
    padding: 16px;
    overflow: hidden;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 8px;
    transition: all 0.3s ease;

    &--compact {
        min-height: 72px;
        padding: 8px 10px;
    }

    &--small {
        min-height: 76px;
        padding: 10px;
    }

    &--medium {
        min-height: 130px;
        padding: 20px;
    }

    &--large {
        min-height: 160px;
        padding: 24px;
    }

    &--loading {
        opacity: 0.8;
    }

    &--create {
        .stat-values {
            margin-top: 0;
        }

        .stat-header {
            margin-bottom: 0;
        }
    }
}

.stat-icon-bg {
    position: absolute;
    top: -20px;
    right: -20px;
    opacity: 0.08;
    pointer-events: none;
    z-index: 0;
}

.stat-main {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    height: 100%;
    flex-grow: 1;
    gap: 6px;
}

.stat-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0;
    gap: 12px;
}

.stat-header-left {
    flex: 1;
    min-width: 0;
}

.stat-title {
    font-size: 0.875rem;
    font-weight: bold;
    color: #585f65;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 4px 0;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
}

.stat-header-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(25, 118, 210, 0.1);
    border-radius: 8px;
}

.stat-warning-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    margin: 0;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(255, 152, 0, 0.12);
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;

    &:hover {
        background: rgba(255, 152, 0, 0.22);
    }

    &--active {
        background: rgba(255, 152, 0, 0.28);
        box-shadow: 0 0 0 2px rgba(255, 152, 0, 0.35);
    }
}

.stat-subtitle {
    font-size: 0.75rem;
    font-weight: 400;
    margin: 0;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--grey {
        color: #9e9e9e;
    }

    &--warning {
        color: #ef6c00;
        font-weight: 600;
    }

    &--error {
        color: #c62828;
        font-weight: 600;
    }
}

.stat-values {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.stat-value-wrapper {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--create {
        align-items: center;
        justify-content: center;
        flex: 1;
        min-height: 40px;
    }
}

.stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.1;
    color: #1976d2;
    margin: 0;
    transition: color 0.3s ease;

    &--negative {
        color: #d32f2f;
    }

    &--loading {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.75rem;
        font-weight: 400;
        font-style: italic;
        color: #9e9e9e;

        span {
            flex: 1;
        }
    }
}

.stat-value-suffix {
    font-size: 0.55em;
    font-weight: 600;
    color: #757575;
    margin-left: 0.35em;
    letter-spacing: 0.02em;
    white-space: nowrap;
}

.stat-loading-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #e0e0e0;
    border-top-color: #1976d2;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.stat-secondary-value {
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.4;
    margin: 0;

    &--grey {
        color: #757575;
    }

    &--loading {
        height: 1em;
    }
}

.stat-accent-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    transition: background 0.3s ease;

    &--negative {
        background: linear-gradient(90deg, #d32f2f 0%, #f44336 100%);
    }
}

// Size variations
.stat-content--compact {
    .stat-header {
        margin-bottom: 0;
        gap: 6px;
    }

    .stat-title {
        font-size: 0.6875rem;
        letter-spacing: 0.35px;
        margin-bottom: 2px;
        -webkit-line-clamp: 3;
        line-clamp: 3;
    }

    .stat-subtitle {
        font-size: 0.625rem;
    }

    .stat-value {
        font-size: 1rem;
    }

    .stat-value--loading {
        font-size: 0.6875rem;
    }

    .stat-secondary-value {
        font-size: 0.6875rem;
    }

    .stat-header-icon {
        width: 24px;
        height: 24px;
    }
}

.stat-content--small {
    .stat-header {
        margin-bottom: 0;
        gap: 8px;
    }

    .stat-title {
        font-size: 0.75rem;
        letter-spacing: 0.4px;
        margin-bottom: 2px;
    }

    .stat-subtitle {
        font-size: 0.6875rem;
    }

    .stat-value {
        font-size: 1.125rem;
    }
    .stat-value--loading {
        font-size: 0.875rem;
    }

    .stat-secondary-value {
        font-size: 0.75rem;
    }

    .stat-header-icon {
        width: 28px;
        height: 28px;
    }

    .stat-values {
        gap: 4px;
    }
}

.stat-content--medium {
    .stat-value {
        font-size: 1.75rem;
    }
    .stat-value--loading {
        font-size: 0.75rem;
    }
}

.stat-content--large {
    .stat-value {
        font-size: 2rem;
    }

    .stat-header-icon {
        width: 48px;
        height: 48px;
    }
}
</style>
